// Pure builder for the tenant Schema.org JSON-LD `@graph`.
//
// Produces a single object with:
//   - Person node (only when ownerName is set) — gets sameAs / knowsAbout
//   - Business node (always) — @type from businessType, configurable address /
//     priceRange / areaServed / identifier[] (NIP/KNF/REGON), founder→Person if any
//   - WebSite node (always) — publisher→Business
//
// Kept side-effect-free so it can be unit-tested without a DOM. Layout.tsx
// wraps the result with injectJsonLd().

export interface SchemaGraphInput {
  siteUrl: string;
  siteName: string;
  businessType?: string;
  description?: string;
  telephone?: string;
  email?: string;
  /** Legacy single-line address (used only if addressLocality is empty). */
  address?: string;
  addressLocality?: string;
  addressCountry?: string;
  areaServedName?: string;
  priceRange?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  sameAsUrls?: string[];
  ownerName?: string;
  ownerAlternateName?: string;
  ownerJobTitle?: string;
  knowsAbout?: string[];
  nip?: string;
  regon?: string;
  knfNumber?: string;
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

type JsonObject = { [key: string]: JsonValue };

function buildAddress(input: SchemaGraphInput): JsonObject | undefined {
  const locality = input.addressLocality?.trim();
  const country = input.addressCountry?.trim();
  if (locality) {
    return {
      '@type': 'PostalAddress',
      addressLocality: locality,
      addressCountry: country || 'PL',
    };
  }
  const flat = input.address?.trim();
  if (flat) {
    return {
      '@type': 'PostalAddress',
      streetAddress: flat,
      addressCountry: country || 'PL',
    };
  }
  return undefined;
}

function buildSameAs(input: SchemaGraphInput): string[] {
  const all = [
    input.facebookUrl,
    input.instagramUrl,
    ...(input.sameAsUrls ?? []),
  ];
  return all
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0);
}

function buildIdentifiers(input: SchemaGraphInput): JsonValue[] | undefined {
  const items: JsonValue[] = [];
  if (input.nip?.trim()) {
    items.push({
      '@type': 'PropertyValue',
      propertyID: 'NIP',
      value: input.nip.trim(),
    });
  }
  if (input.knfNumber?.trim()) {
    items.push({
      '@type': 'PropertyValue',
      propertyID: 'KNF',
      value: input.knfNumber.trim(),
    });
  }
  if (input.regon?.trim()) {
    items.push({
      '@type': 'PropertyValue',
      propertyID: 'REGON',
      value: input.regon.trim(),
    });
  }
  return items.length > 0 ? items : undefined;
}

function clean<T extends Record<string, JsonValue | undefined>>(obj: T): T {
  const out: Record<string, JsonValue> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out as T;
}

export function buildSchemaGraph(input: SchemaGraphInput): {
  '@context': 'https://schema.org';
  '@graph': JsonValue[];
} {
  const siteUrl = input.siteUrl.replace(/\/$/, '');
  const personId = `${siteUrl}/#person`;
  const businessId = `${siteUrl}/#business`;
  const websiteId = `${siteUrl}/#website`;

  const businessType = input.businessType?.trim() || 'LocalBusiness';
  const address = buildAddress(input);
  const sameAs = buildSameAs(input);
  const identifiers = buildIdentifiers(input);
  const areaServed = {
    '@type': 'Country',
    name: input.areaServedName?.trim() || 'Poland',
  };

  const hasOwner = !!input.ownerName?.trim();
  const graph: JsonValue[] = [];

  if (hasOwner) {
    const knows = (input.knowsAbout ?? [])
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    graph.push(
      clean({
        '@type': 'Person',
        '@id': personId,
        name: input.ownerName!.trim(),
        alternateName: input.ownerAlternateName?.trim(),
        jobTitle: input.ownerJobTitle?.trim(),
        url: siteUrl + '/',
        telephone: input.telephone?.trim(),
        email: input.email?.trim(),
        address,
        worksFor: { '@id': businessId },
        sameAs: sameAs.length > 0 ? sameAs : undefined,
        knowsAbout: knows.length > 0 ? knows : undefined,
      }) as unknown as JsonValue,
    );
  }

  graph.push(
    clean({
      '@type': businessType,
      '@id': businessId,
      name: input.siteName,
      url: siteUrl + '/',
      telephone: input.telephone?.trim(),
      email: input.email?.trim(),
      description: input.description?.trim(),
      priceRange: input.priceRange?.trim(),
      areaServed,
      address,
      // sameAs lives on the Person when one exists; otherwise put it on the
      // business so non-personal-brand tenants still get social links.
      sameAs: !hasOwner && sameAs.length > 0 ? sameAs : undefined,
      founder: hasOwner ? { '@id': personId } : undefined,
      identifier: identifiers,
      potentialAction: {
        '@type': 'ReserveAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/rezerwacja`,
          actionPlatform: [
            'http://schema.org/DesktopWebPlatform',
            'http://schema.org/MobileWebPlatform',
          ],
        },
        result: { '@type': 'Reservation', name: 'Rezerwacja wizyty' },
      },
    }) as unknown as JsonValue,
  );

  graph.push({
    '@type': 'WebSite',
    '@id': websiteId,
    url: siteUrl + '/',
    name: input.siteName,
    inLanguage: 'pl-PL',
    publisher: { '@id': businessId },
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
